import React, {useEffect, useRef, useState} from "react";
import {CloudUpload, Image as ImageIcon, FileText, Trash2, Link2, CheckCircle2} from "lucide-react";
import {useResourcePersonUploads} from "../../store/resourcePerson/resourcePersonStore";
import {IMAGE_MAX_BYTES, DOCUMENT_MAX_BYTES} from "../../store/resourcePerson/resourcePersonUploadStore";
import {IMAGE_HOST} from "../../constants";
import {toDisplayString} from "../../utils/toDisplayString";
import Toast from "../Toast";

const COURSE_COVER_UPLOAD_TARGET = 900 * 1024;

async function optimizeCourseCover(file) {
  if (file.size <= COURSE_COVER_UPLOAD_TARGET || file.type === "image/gif" || typeof createImageBitmap !== "function") {
    return file;
  }

  let bitmap;
  try {
    bitmap = await createImageBitmap(file);
  } catch {
    return file;
  }

  try {
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    if (!ctx) return file;
    let smallestBlob = null;
    for (const maxWidth of [1600, 1280, 960]) {
      const scale = Math.min(1, maxWidth / bitmap.width, (maxWidth * 0.625) / bitmap.height);
      canvas.width = Math.max(1, Math.round(bitmap.width * scale));
      canvas.height = Math.max(1, Math.round(bitmap.height * scale));
      ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height);

      for (const quality of [0.84, 0.72, 0.60]) {
        const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/webp", quality));
        if (!blob) continue;
        if (!smallestBlob || blob.size < smallestBlob.size) smallestBlob = blob;
        if (blob.size <= COURSE_COVER_UPLOAD_TARGET) {
          const baseName = file.name.replace(/\.[^.]+$/, "");
          return new File([blob], `${baseName}.webp`, {type: "image/webp", lastModified: file.lastModified});
        }
      }
    }
    if (smallestBlob && smallestBlob.size < file.size) {
      const baseName = file.name.replace(/\.[^.]+$/, "");
      return new File([smallestBlob], `${baseName}.webp`, {type: "image/webp", lastModified: file.lastModified});
    }
  } finally {
    bitmap.close?.();
  }

  return file;
}

/**
 * UploadField — Cloudinary upload through the backend.
 *
 * kind="image"    → POST /api/uploads/image?folder=… (≤10MB, public)
 * kind="document" → POST /api/uploads/document      (≤50MB, private + signed previewUrl)
 *
 * Raw binary body + Content-Type/x-filename (never FormData).
 *
 * Props: { kind, folder, label, accept, value, onChange(url, upload), disabled,
 *          hint, allowUrlPaste, preview }
 *
 * Renders a single dropzone card that doubles as its own preview, so the author
 * sees what they picked in place — instead of a raw "No file chosen" input, a
 * size warning in kilobytes, and a separate URL box stacked underneath.
 */
const UploadField = ({
  kind = "image",
  folder,
  label,
  accept,
  value,
  onChange,
  disabled,
  hint,
  maxSizeBytes,
  allowUrlPaste = true,
  preview = true,
}) => {
  const isImage = kind !== "document";
  const {
    imageUploadLoading,
    imageUploadError,
    documentUploadLoading,
    documentUploadError,
    uploadImage,
    uploadDocument,
  } = useResourcePersonUploads();

  const [toast, setToast] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [showUrl, setShowUrl] = useState(false);
  const [urlDraft, setUrlDraft] = useState("");
  const busy = isImage ? imageUploadLoading : documentUploadLoading;
  const error = isImage ? imageUploadError : documentUploadError;

  // Clear the file input after each pick so the same file can be re-chosen.
  const [inputKey, setInputKey] = useState(0);
  const inputRef = useRef(null);
  useEffect(() => {
    if (!busy) setInputKey((key) => key + 1);
  }, [busy]);

  const upload = async (file) => {
    if (!file) return;
    // Pre-flight size check: reject locally so an oversized file never travels
    // to the server. The API's proxy answers an oversized body with its own
    // 413 page, and blowing the request up wastes the whole upload first.
    const limit = isImage ? (maxSizeBytes || IMAGE_MAX_BYTES) : DOCUMENT_MAX_BYTES;
    if (file.size > limit) {
      const limitMb = Math.round(limit / (1024 * 1024));
      setToast(
        isImage
          ? `That image is too large. Please use an image no larger than ${limitMb}MB.`
          : "That document is too large. Please use a file under 50MB."
      );
      return;
    }
    const uploadFile = isImage && folder === "course-covers" ? await optimizeCourseCover(file) : file;
    const result = isImage ? await uploadImage(uploadFile, folder) : await uploadDocument(file);
    if (!result.success) {
      setToast(toDisplayString(result.error, "Upload failed."));
      return;
    }
    const payload = result.upload || {};
    const url =
      payload.secureUrl || payload.secure_url || payload.url ||
      payload.previewUrl || payload.preview_url || "";
    onChange?.(url, payload);
  };

  const pick = (event) => upload(event.target.files?.[0]);

  // Cloudinary stores a relative public id; preview it through the image host.
  const previewSrc =
    value ? (String(value).startsWith("http") ? String(value) : `${IMAGE_HOST || ""}${value}`) : "";

  const onDrop = (event) => {
    event.preventDefault();
    setDragOver(false);
    if (disabled || busy) return;
    upload(event.dataTransfer?.files?.[0]);
  };

  const acceptText = accept || (isImage ? "image/*" : ".pdf,.doc,.docx,.ppt,.pptx,.txt");
  const imageLimitMb = Math.round((maxSizeBytes || IMAGE_MAX_BYTES) / (1024 * 1024));
  const hintText =
    hint || (isImage ? `JPG, PNG, WebP or GIF · up to ${imageLimitMb} MB` : "PDF or Office docs · up to 50 MB");

  const zoneIdle = dragOver
    ? "border-blue-400 bg-blue-50/70"
    : "border-slate-300 bg-slate-50/60 hover:border-blue-300 hover:bg-blue-50/40";
  const hasImagePreview = preview && value && isImage;
  const shell = [
    "relative w-full overflow-hidden rounded-lg border-2 border-dashed transition",
    hasImagePreview ? "max-w-[300px]" : "",
    disabled || busy ? "cursor-not-allowed opacity-70" : "cursor-pointer",
    zoneIdle,
  ].join(" ");

  const stop = (event) => event.stopPropagation();

  return (
    <div>
      {label ? (
        <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</span>
      ) : null}
      {/* ---------- Dropzone, which doubles as the preview ---------- */}
      <div
        className={shell}
        onDragOver={(event) => {
          event.preventDefault();
          if (!disabled && !busy) setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        onClick={() => !disabled && !busy && inputRef.current?.click()}
        role="button"
        tabIndex={disabled ? -1 : 0}
        onKeyDown={(event) => {
          if ((event.key === "Enter" || event.key === " ") && !disabled && !busy) {
            event.preventDefault();
            inputRef.current?.click();
          }
        }}
        aria-label={label || (isImage ? "Upload image" : "Upload document")}
      >
        <input
          key={inputKey}
          ref={inputRef}
          type="file"
          accept={acceptText}
          disabled={disabled || busy}
          onChange={pick}
          onClick={stop}
          className="sr-only"
        />

        {busy ? (
          <div className="flex flex-col items-center justify-center gap-2 px-4 py-10">
            <span className="h-6 w-6 animate-spin rounded-full border-2 border-slate-300 border-t-blue-600" />
            <span className="text-xs font-medium text-slate-600">Uploading…</span>
          </div>
        ) : preview && value && isImage ? (
          <div className="relative">
            <img
              src={previewSrc}
              alt="Selected image preview"
              className="aspect-[16/10] w-full bg-slate-100 object-cover object-top"
            />
            <div className="absolute inset-x-0 bottom-0 flex items-center justify-end gap-1.5 bg-gradient-to-t from-black/70 to-transparent p-2">
              <button
                type="button"
                disabled={disabled}
                onClick={(event) => {
                  stop(event);
                  inputRef.current?.click();
                }}
                className="inline-flex items-center gap-1 rounded-md bg-white/90 px-2.5 py-1 text-xs font-semibold text-slate-800 hover:bg-white disabled:opacity-60"
              >
                <CloudUpload className="h-3.5 w-3.5" /> Replace
              </button>
              <button
                type="button"
                disabled={disabled}
                onClick={(event) => {
                  stop(event);
                  onChange?.("", null);
                  setUrlDraft("");
                }}
                className="inline-flex items-center gap-1 rounded-md bg-white/90 px-2.5 py-1 text-xs font-semibold text-red-700 hover:bg-white disabled:opacity-60"
              >
                <Trash2 className="h-3.5 w-3.5" /> Remove
              </button>
            </div>
          </div>
        ) : preview && value ? (
          /* Document → a tidy file chip rather than an image strip. */
          <div className="flex items-center gap-3 px-4 py-4">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-blue-50 text-blue-600">
              <FileText className="h-5 w-5" />
            </span>
            <span className="min-w-0 flex-1">
              <span className="block truncate text-sm font-medium text-slate-800" title={value}>
                {String(value).split("/").pop()}
              </span>
              <span className="flex items-center gap-1 text-xs text-emerald-600">
                <CheckCircle2 className="h-3.5 w-3.5" /> Uploaded
              </span>
            </span>
            <button
              type="button"
              disabled={disabled}
              onClick={(event) => {
                stop(event);
                onChange?.("", null);
              }}
              className="inline-flex shrink-0 items-center gap-1 rounded-md border border-slate-200 px-2.5 py-1 text-xs font-semibold text-red-700 hover:bg-red-50 disabled:opacity-60"
            >
              <Trash2 className="h-3.5 w-3.5" /> Remove
            </button>
          </div>
        ) : (
          /* Empty state — icon, action, and a calm human hint. */
          <div className="flex flex-col items-center justify-center gap-1.5 px-4 py-8 text-center">
            <span className="flex h-11 w-11 items-center justify-center rounded-full bg-white text-blue-600 shadow-sm">
              {isImage ? <ImageIcon className="h-5 w-5" /> : <FileText className="h-5 w-5" />}
            </span>
            <span className="text-sm font-medium text-slate-700">
              {dragOver ? "Drop it here" : "Drag & drop, or click to browse"}
            </span>
            <span className="text-xs text-slate-500">{hintText}</span>
            {isImage && folder === "course-covers" ? (
              <span className="text-[11px] text-slate-400">Large covers are optimized automatically before upload.</span>
            ) : null}
          </div>
        )}
      </div>

      {error ? (
        <p className="m-0 mt-1.5 text-xs text-amber-700">{toDisplayString(error, "Upload failed.")}</p>
      ) : null}
      {/* ---------- Optional "paste a link" row ---------- */}
      {allowUrlPaste ? (
        showUrl ? (
          <div className="mt-2 flex items-center gap-2">
            <input
              type="url"
              value={urlDraft}
              disabled={disabled}
              onChange={(event) => setUrlDraft(event.target.value)}
              onKeyDown={(event) => {
                if (event.key !== "Enter") return;
                event.preventDefault();
                if (urlDraft.trim()) onChange?.(urlDraft.trim(), null);
                setShowUrl(false);
              }}
              placeholder="https://…"
              className="w-full rounded-md border border-slate-200 px-3 py-2 text-xs outline-none focus:border-blue-300 disabled:bg-slate-100"
            />
            <button
              type="button"
              disabled={disabled || !urlDraft.trim()}
              onClick={() => {
                onChange?.(urlDraft.trim(), null);
                setShowUrl(false);
              }}
              className="shrink-0 rounded-md bg-blue-600 px-3 py-2 text-xs font-semibold text-white hover:bg-blue-500 disabled:opacity-50"
            >
              Use link
            </button>
            <button
              type="button"
              onClick={() => {
                setShowUrl(false);
                setUrlDraft("");
              }}
              className="shrink-0 rounded-md border border-slate-200 px-3 py-2 text-xs font-semibold text-slate-600 hover:bg-slate-50"
            >
              Cancel
            </button>
          </div>
        ) : (
          <button
            type="button"
            disabled={disabled}
            onClick={() => setShowUrl(true)}
            className="mt-1.5 inline-flex items-center gap-1 text-xs font-medium text-slate-500 hover:text-blue-600 disabled:opacity-60"
          >
            <Link2 className="h-3.5 w-3.5" />
            {value ? "Use a different image" : "Or paste an image link"}
          </button>
        )
      ) : null}

      {toast ? <Toast message={toast} onClose={() => setToast(null)} /> : null}
    </div>
  );
};

export default UploadField;
